import { createBrowserRouter, Navigate, Outlet, useLocation } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import App from './App'
import { AuthenticatedLayout } from '@/layouts/authenticated'
import { useAppContext } from '@/layouts/app-layout'
import { ErrorBoundary } from '@/components/error-boundary'
import { useAuthStore } from '@/lib/auth'

import { RouteErrorBoundary } from '@/components/route-error-boundary'
import { AppShellSkeleton } from '@/components/app-shell-skeleton'

// Helper: map default export → { Component } for react-router lazy
function lazy(loader: () => Promise<{ default: React.ComponentType }>) {
  return async () => {
    const mod = await loader()
    return { Component: mod.default }
  }
}

// Lazy-loaded auth pages (no shell)
const LoginPage = lazy(() => import('@/pages/auth/login'))
const RegisterPage = lazy(() => import('@/pages/auth/register'))
const OnboardingPage = lazy(() => import('@/pages/auth/onboarding'))
const AcceptTermsPage = lazy(() => import('@/pages/auth/accept-terms'))
const MagicLinkPage = lazy(() => import('@/pages/auth/magic-link'))
const MagicExpiredPage = lazy(() => import('@/pages/auth/magic-expired'))
const ReactivatePage = lazy(() => import('@/pages/billing/reactivate'))

// Lazy-loaded app pages
const DashboardPage = lazy(() => import('@/pages/dashboard/overview'))
const ClientsListPage = lazy(() => import('@/pages/clients/clients-list'))
const DeadlinesListPage = lazy(() => import('@/pages/deadlines/deadlines-list'))
const CasesListPage = lazy(() => import('@/pages/cases/cases-list'))

// Studio
const StudioProfilePage = lazy(() => import('@/pages/studio/profile'))
const StudioTeamPage = lazy(() => import('@/pages/studio/team'))

const StudioAssessmentPage = lazy(() => import('@/pages/studio/assessment'))

// Settings
const SettingsAccountPage = lazy(() => import('@/pages/settings/account'))
const SettingsAuditPage = lazy(() => import('@/pages/settings/audit'))

// Billing
const BillingWalletPage = lazy(() => import('@/pages/billing/wallet'))
const BillingTopupPage = lazy(() => import('@/pages/billing/topup'))

// Route-level guard for billing routes
function BillingGuard() {
  const ctx = useAppContext()
  if (!ctx.can('wallet', 'update')) return <Navigate to="/dashboard" replace />
  return <Outlet context={ctx} />
}

function RequireStudioProfile() {
  const ctx = useAppContext()
  const location = useLocation()
  const user = useAuthStore((s) => s.user)
  const studioProfileCompleted = user?.studioProfileCompleted
  const isStudioProfileRoute = location.pathname === '/studio' || location.pathname === '/studio/profile'

  if (studioProfileCompleted === undefined) return null

  // Il redirect a /studio/profile vale SOLO per il TENANT_ADMIN: è l'unico
  // ruolo che può effettivamente compilare i dati dello studio. Gli altri
  // ruoli (EDITOR/CONTRIBUTOR/MEMBER) hanno solo `tenant_profile:read` —
  // un loro redirect li bloccherebbe in una pagina read-only, senza modo
  // di proseguire. Per loro lasciamo passare; l'eventuale banner in
  // dashboard avvisa che l'admin deve completare i dati.
  const isTenantAdmin = user?.roles?.includes('TENANT_ADMIN') ?? false

  if (isTenantAdmin && !studioProfileCompleted && !isStudioProfileRoute) {
    const redirect = `${location.pathname}${location.search}`
    return (
      <Navigate
        to={`/studio/profile?required=studio-profile&redirect=${encodeURIComponent(redirect)}`}
        replace
      />
    )
  }

  return <Outlet context={ctx} />
}

// Placeholder for pages not yet migrated
function ComingSoon() {
  const { t } = useTranslation('common')
  return (
    <div className="flex flex-1 flex-col items-center justify-center gap-3 text-muted-foreground py-16">
      <p className="text-sm font-medium">{t('common.comingSoon')}</p>
    </div>
  )
}

export const router = createBrowserRouter([
  {
    path: '/',
    element: <App />,
    errorElement: <RouteErrorBoundary />,
    hydrateFallbackElement: <AppShellSkeleton />,
    children: [
      // ── Auth routes (no shell) ──
      { path: 'login', lazy: LoginPage },
      { path: 'register', lazy: RegisterPage },
      { path: 'onboarding', lazy: OnboardingPage },
      // /accept-terms: privacy gate per utenti invitati al primo login.
      // Sta fuori dall'AuthenticatedLayout per non far scattare il subscription
      // gate / studio profile gate prima di aver raccolto il consenso.
      { path: 'accept-terms', lazy: AcceptTermsPage },
      { path: 'auth/link', lazy: () => import('./pages/auth/login-link') },
      // Token lives in the URL fragment (`/magic#token=...`), not the path:
      // fragments are not sent to the server, not stored in Referer headers,
      // and not visible to access logs. Same pattern as `/auth/link`.
      { path: 'magic', lazy: MagicLinkPage },
      { path: 'magic/expired', lazy: MagicExpiredPage },
      { path: 'billing/reactivate', lazy: ReactivatePage },

      // ── App routes (authenticated, with shell) ──
      {
        element: <ErrorBoundary><AuthenticatedLayout /></ErrorBoundary>,
        children: [
          { index: true, element: <Navigate to="/dashboard" replace /> },
          {
            element: <RequireStudioProfile />,
            children: [
              // Dashboard
              { path: 'dashboard', lazy: DashboardPage },

              // Clienti
              { path: 'clients', lazy: ClientsListPage },
              { path: 'clients/new', lazy: ClientsListPage },
              { path: 'clients/:id', lazy: ClientsListPage },

              // Fascicoli AML
              { path: 'cases', lazy: CasesListPage },
              { path: 'cases/new', lazy: CasesListPage },
              { path: 'cases/:id', lazy: CasesListPage },

              // Scadenze
              { path: 'deadlines', lazy: DeadlinesListPage },
              { path: 'deadlines/create', lazy: DeadlinesListPage },

              // Studio
              { path: 'studio', element: <Navigate to="/studio/profile" replace /> },
              { path: 'studio/profile', lazy: StudioProfilePage },
              { path: 'studio/team', lazy: StudioTeamPage },
              { path: 'studio/assessment', lazy: StudioAssessmentPage },

              // Impostazioni (roles + permissions escluse)
              { path: 'settings', lazy: SettingsAccountPage },
              { path: 'settings/studio', element: <Navigate to="/studio/profile" replace /> },
              { path: 'settings/audit', lazy: SettingsAuditPage },

              // Billing (gated: can('wallet', 'update'))
              {
                element: <BillingGuard />,
                children: [
                  { path: 'billing', lazy: BillingWalletPage },
                  { path: 'billing/topup', lazy: BillingTopupPage },
                ],
              },
            ],
          },
        ],
      },
    ],
  },
])
